import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

export const EmailEditorQuill = forwardRef(({ initialHtml, onHtmlChange, onFocus, disabled }, ref) => {
  const [content, setContent] = useState(initialHtml || '');
  const quillRef = useRef(null);

  useEffect(() => {
    setContent(initialHtml || '');
  }, [initialHtml]);

  const handleChange = value => {
    if (disabled) return;
    setContent(value);
    if (onHtmlChange) onHtmlChange(value);
  };

  const focusEditor = () => {
    if (disabled) return;
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;
    quill.focus();
  };

  useImperativeHandle(ref, () => ({
    insertTokenAtCursor: tokenText => {
      const quill = quillRef.current?.getEditor?.();
      if (!quill) return;

      const range = quill.getSelection(true);
      const index = range ? range.index : quill.getLength();

      const textToInsert = ` ${tokenText}`;
      quill.insertText(index, textToInsert);
      quill.setSelection(index + textToInsert.length, 0);
    }
  }));

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      [{ font: [] }],
      [{ size: ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ align: [] }],
    ],
    history: {
      delay: 500,
      maxStack: 100,
      userOnly: true
    },
    clipboard: {
      matchVisual: false
    }
  };

  const formats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'script',
    'list',
    'bullet',
    'check',
    'indent',
    'align',
    'blockquote',
    'code-block',
    'link'
  ];

  return (
    <div
      onClick={focusEditor}
      style={{
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #ccc',
        borderRadius: 6,
        marginTop: 4,
        marginBottom: 16,
        overflow: 'hidden',
        minHeight: '240px',
        cursor: disabled ? 'default' : 'text'
      }}
    >
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={content}
        onChange={handleChange}
        onFocus={onFocus}
        modules={modules}
        formats={formats}
        disabled={disabled}
        preserveWhitespace={true}
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '240px'
        }}
      />
      <style>
        {`
          .ql-container {
            min-height: 240px;
          }

          .ql-editor {
            min-height: 240px;
            cursor: text;
          }
        `}
      </style>
    </div>
  );
});
